import { MouseEvent, useEffect, useRef } from "react";
import { CSSTransition } from "react-transition-group";
import Textarea from "@/components/Textarea";
import { isArray } from "lodash";
import { Button, ButtonType } from "@/components/common/button";
import { QuizQuestionProps } from "@/pages/quiz/context/types";
import { getUpdatedExclusiveQuestionValue } from "@/utils/getUpdatedExclusiveQuestionValue";
import { InnerBlockType } from "@/types";
import { ErrorInnerBlock } from "@/components/common/errorInnerBlock";

export type MultipleChoiceAnswer = {
  answer?: string[];
  inputValue?: Record<string, string | undefined>;
};

const MultipleChoiceQuestion = ({
  question,
  questionValue,
  setQuestionAnswer,
  setQuestionValue,
}: QuizQuestionProps<MultipleChoiceAnswer>) => {
  const { options } = question.attributes;

  useEffect(() => {
    if (questionValue && isArray(questionValue)) {
      setQuestionValue({
        answer: questionValue,
      });
    }
  }, [questionValue]);

  const handleCheckboxChange = (value: string) => {
    const updatedVal = getUpdatedExclusiveQuestionValue(value, questionValue);
    setQuestionValue(updatedVal);
    // const isSendNoneResponse =
    //   updatedVal &&
    //   updatedVal?.length === 1 &&
    //   updatedVal[0] === "none" &&
    //   value === "none";
    // if (isSendNoneResponse) {
    //   setQuestionAnswer({
    //     ...questionValue,
    //     answer: updatedVal,
    //   });
    //   return;
    // }
  };

  const handleTextareaInputChange =
    (value: string) => (textareaValue: string) => {
      setQuestionValue({
        ...questionValue,
        inputValue: {
          ...(questionValue?.inputValue ?? {}),
          [value]: textareaValue,
        },
      });
    };

  const nodeRef = useRef(null);

  const handleClick = (event: MouseEvent, value: string) => {
    event.preventDefault();
    handleCheckboxChange(value);
  };

  const hasEmptyTextArea = questionValue.answer?.some((answer) => {
    const option = options?.find((option) => option.value === answer);

    if (option?.innerBlock && !questionValue.inputValue?.[option.value]?.trim()) {
      return true;
    }

    return false;
  });

  return (
    <div className="my-4 sm:my-6 lg:my-8 flex flex-col space-y-3 sm:space-y-4">
      {options?.map(({ value, label, innerBlock }) => (
        <label
          key={value}
          htmlFor={value}
          className="group p-3 sm:p-5 lg:p-6 flex border border-slate-gray rounded-md cursor-pointer hover:border-dark-purple"
          onClick={(e) => handleClick(e, value)}
        >
          <div className="custom-checkbox pt-1">
            <input
              type="checkbox"
              id={value}
              value={value}
              checked={questionValue.answer?.includes(value)}
              onChange={({ target }) => handleCheckboxChange(target.value)}
              className="w-3.5 sm:w-4 lg:w-5 h-3.5 sm:h-4 lg:h-5 cursor-pointer hidden-checkbox"
            />
            <div className="styled-checkbox" />
          </div>
          <div className="grow">
            <p className="text-base sm:text-lg lg:text-xl pl-6 sm:pl-7 lg:pl-8 text-charcoal font-bold">
              {label}
            </p>
            {/* {value === "none" && (
                <span className="block text-sm pl-6 sm:pl-7 lg:pl-8">
                  (Best answer)
                </span>
              )} */}
            <CSSTransition
              nodeRef={nodeRef}
              in={innerBlock && questionValue.answer?.includes(value)}
              timeout={300}
              classNames="fade"
              unmountOnExit
            >
              <label
                ref={nodeRef}
                htmlFor={innerBlock?.label}
                className="gap-1.5 sm:gap-2.5 mt-4 sm:mt-5 flex flex-col"
              >
                {innerBlock?.type === InnerBlockType.Error ? (
                  <ErrorInnerBlock label={innerBlock.label} className="text-red" />
                ) : (
                  <span className="text-sm sm:text-base lg:text-[17px] font-normal">
                    {innerBlock?.label}
                  </span>
                )}
                {innerBlock?.type === InnerBlockType.Textarea && (
                  <Textarea
                    id={innerBlock.label}
                    placeholder={innerBlock.placeholder}
                    value={questionValue.inputValue?.[value]}
                    onChange={handleTextareaInputChange(value)}
                    onClick={(e: MouseEvent<HTMLTextAreaElement>) =>
                      e.stopPropagation()
                    }
                  />
                )}
              </label>
            </CSSTransition>
          </div>
        </label>
      ))}
      <Button
        disabled={!questionValue?.answer?.length || hasEmptyTextArea}
        onClick={() => setQuestionAnswer(questionValue)}
        type={ButtonType.CONTINUE}
      />
    </div>
  );
};

export { MultipleChoiceQuestion };
